import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { HelpCircle, Loader2, Plus, Edit2, Trash2, Ticket, Clock, Users, AlertCircle, Search, Building2, ListTree } from "lucide-react";
import ViewSelector from "../components/ViewSelector";
import ListView from "../components/ListView";
import SubCategoriasTab from "../components/tickets/SubCategoriasTab";

export default function CategoriasTicketPage({ modulo = null, embedded = false }) {
  const [categorias, setCategorias] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [searchQuery, setSearchQuery] = useState("");
  const { getAuthHeaders } = useAuth();
  // Cabeceras con módulo (Hallazgos Auditoria) cuando aplica
  const authH = useCallback(() => {
    const base = (getAuthHeaders)();
    return modulo ? { ...base, headers: { ...base.headers, "X-Modulo": modulo } } : base;
  }, [getAuthHeaders, modulo]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#3B82F6",
    sla_hours: 24,
    prioridad_id: "",
    departamento_id: "",
    usuarios_asignados: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [catRes, prioRes, usersRes, deptRes] = await Promise.all([
        axios.get(`${API}/tickets/categorias`, authH()),
        axios.get(`${API}/tickets/prioridades`, authH()),
        axios.get(`${API}/tickets/usuarios-disponibles`, authH()),
        axios.get(`${API}/departamentos`, authH())
      ]);
      setCategorias(catRes.data);
      setPrioridades(prioRes.data);
      setUsuarios(usersRes.data);
      setDepartamentos(deptRes.data);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({ 
      name: "", 
      description: "", 
      color: "#3B82F6", 
      sla_hours: 24,
      prioridad_id: "",
      departamento_id: "",
      usuarios_asignados: []
    });
    setEditingCategoria(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (cat) => {
    setEditingCategoria(cat);
    setFormData({
      name: cat.name,
      description: cat.description || "",
      color: cat.color || "#3B82F6",
      sla_hours: cat.sla_hours || 24,
      prioridad_id: cat.prioridad_id || "",
      departamento_id: cat.departamento_id || "",
      usuarios_asignados: cat.usuarios_asignados || []
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      if (editingCategoria) {
        await axios.put(`${API}/tickets/categorias/${editingCategoria.id}`, formData, authH());
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/tickets/categorias`, formData, authH());
        toast.success("Categoría creada");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const deleteCategoria = async (id) => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    try {
      await axios.delete(`${API}/tickets/categorias/${id}`, authH());
      toast.success("Categoría eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const colors = [
    { value: "#3B82F6", label: "Azul" },
    { value: "#10B981", label: "Verde" },
    { value: "#F59E0B", label: "Naranja" },
    { value: "#EF4444", label: "Rojo" },
    { value: "#8B5CF6", label: "Morado" },
    { value: "#EC4899", label: "Rosa" },
    { value: "#6B7280", label: "Gris" }
  ];

  return (
    <div className={embedded ? "flex" : "flex min-h-screen bg-slate-50 dark:bg-slate-900"}>
      {!embedded && <Sidebar />}
      
      <main className={`flex-1 p-4 md:p-8 ${embedded ? "pt-4 md:pt-4" : "pt-14 md:pt-8 ml-0 md:ml-64"} overflow-x-hidden`} data-testid="categorias-ticket-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Departamento a asignar
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Configura los departamentos a los que se pueden asignar tickets, sus usuarios y las subcategorías condicionales
            </p>
          </div>
        </div>

        <Tabs defaultValue="categorias" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="categorias" data-testid="tab-departamentos-asignar">
              <Building2 className="w-4 h-4 mr-2" />
              Departamentos a asignar
            </TabsTrigger>
            <TabsTrigger value="subcategorias" data-testid="tab-sub-categorias">
              <ListTree className="w-4 h-4 mr-2" />
              Sub categorías
            </TabsTrigger>
          </TabsList>

          <TabsContent value="categorias">
            <div className="flex justify-end mb-4">
              <Button onClick={openNewDialog} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white" data-testid="nueva-categoria-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Departamento a asignar
              </Button>
            </div>

        {/* Search & View Selector */}
        <div className="flex flex-wrap gap-4 mb-6 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar categorías..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-categorias"
            />
          </div>
          <ViewSelector 
            currentView={viewMode} 
            onViewChange={setViewMode} 
            availableViews={['cards', 'list']}
          />
        </div>

        {(() => {
          const filteredCategorias = categorias.filter(cat =>
            cat.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            cat.description?.toLowerCase().includes(searchQuery.toLowerCase())
          );

          if (loading) {
            return (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            );
          }

          if (filteredCategorias.length === 0) {
            return (
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="py-12 text-center">
                  <HelpCircle className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400 mb-4">
                    {searchQuery ? "No se encontraron categorías" : "No hay categorías"}
                  </p>
                  {!searchQuery && (
                    <Button onClick={openNewDialog} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Crear primera categoría
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          }

          if (viewMode === 'list') {
            return (
              <ListView
                items={filteredCategorias}
                onItemClick={openEditDialog}
                renderItem={(cat) => (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{cat.name}</p>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5 ml-6">
                        {cat.description || 'Sin descripción'} • {cat.tickets_count || 0} tickets
                      </p>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {cat.usuarios_asignados_nombres?.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {cat.usuarios_asignados_nombres.length} usuarios
                        </Badge>
                      )}
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(cat); }}>
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteCategoria(cat.id); }}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              />
            );
          }

          // Default: Cards view
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCategorias.map((cat) => (
                <Card key={cat.id} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openEditDialog(cat)}>
                  <div className="h-2" style={{ backgroundColor: cat.color }} />
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${cat.color}20` }}
                        >
                          <HelpCircle className="w-5 h-5" style={{ color: cat.color }} />
                        </div>
                        <div>
                          <CardTitle className="text-lg text-slate-900 dark:text-white">{cat.name}</CardTitle>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(cat); }}>
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteCategoria(cat.id); }}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {cat.description && (
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{cat.description}</p>
                    )}
                    
                    {cat.prioridad_name && (
                      <div className="flex items-center gap-2 mb-3">
                        <AlertCircle className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-600 dark:text-slate-300">
                          Prioridad: <Badge variant="outline" className="ml-1">{cat.prioridad_name}</Badge>
                        </span>
                      </div>
                    )}
                    
                    {cat.usuarios_asignados_nombres?.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className="text-xs text-slate-600 dark:text-slate-300">
                            Usuarios asignados ({cat.usuarios_asignados_nombres.length}):
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {cat.usuarios_asignados_nombres.slice(0, 3).map((u) => (
                            <Badge key={u.id} variant="secondary" className="text-xs">
                              {u.name}
                            </Badge>
                          ))}
                          {cat.usuarios_asignados_nombres.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{cat.usuarios_asignados_nombres.length - 3} más
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
                      <Ticket className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        {cat.tickets_count || 0} tickets
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        })()}
          </TabsContent>

          <TabsContent value="subcategorias">
            <SubCategoriasTab
              categorias={categorias}
              getAuthHeaders={authH}
            />
          </TabsContent>
        </Tabs>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#0C314C]" />
                {editingCategoria ? "Editar Departamento a asignar" : "Nuevo Departamento a asignar"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nombre de la categoría"
                  data-testid="categoria-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descripción de la categoría"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Color</Label>
                <Select
                  value={formData.color}
                  onValueChange={(value) => setFormData({...formData, color: value})}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: formData.color }}
                        />
                        {colors.find(c => c.value === formData.color)?.label || "Color"}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {colors.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: color.value }}
                          />
                          {color.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Prioridad */}
              <div className="space-y-2">
                <Label>Prioridad por defecto</Label>
                <Select
                  value={formData.prioridad_id || "none"}
                  onValueChange={(value) => setFormData({...formData, prioridad_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar prioridad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin prioridad predefinida</SelectItem>
                    {prioridades.map((prio) => (
                      <SelectItem key={prio.id} value={prio.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: prio.color }}
                          />
                          {prio.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Los tickets de esta categoría heredarán esta prioridad automáticamente
                </p>
              </div>
              
              {/* Departamento vinculado: deriva automáticamente los usuarios que atienden */}
              <div className="space-y-2">
                <Label>Departamento vinculado</Label>
                <Select
                  value={formData.departamento_id || "none"}
                  onValueChange={(value) => setFormData({...formData, departamento_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="categoria-departamento-select">
                    <SelectValue placeholder="Seleccionar departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin departamento</SelectItem>
                    {departamentos.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.nombre || dept.name || "Sin nombre"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Los usuarios que pueden atender se toman automáticamente de los usuarios activos de este departamento.
                </p>
              </div>

              {/* Usuarios que pueden atender (derivados, solo lectura) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400" />
                  Usuarios que pueden atender tickets
                </Label>
                {(() => {
                  const usuariosDept = formData.departamento_id
                    ? usuarios.filter(u => (u.departamento_id === formData.departamento_id) && (u.is_active !== false))
                    : [];
                  if (!formData.departamento_id) {
                    return (
                      <p className="text-sm text-slate-500 border rounded-md p-3 bg-slate-50 dark:bg-slate-900">
                        Selecciona un departamento vinculado para ver los usuarios.
                      </p>
                    );
                  }
                  if (usuariosDept.length === 0) {
                    return (
                      <p className="text-sm text-amber-600 border rounded-md p-3 bg-amber-50 dark:bg-amber-900/20">
                        No hay usuarios activos en este departamento.
                      </p>
                    );
                  }
                  return (
                    <ScrollArea className="h-44 border rounded-md p-3" data-testid="categoria-usuarios-preview">
                      <div className="space-y-2">
                        {usuariosDept.map((u) => (
                          <div key={u.id} className="flex items-center gap-2 text-sm">
                            <div className="w-2 h-2 rounded-full bg-green-500" />
                            <span className="font-medium text-slate-900 dark:text-white">{u.name}</span>
                            <span className="text-slate-500 dark:text-slate-400">({u.email})</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  );
                })()}
                <p className="text-xs text-slate-500">
                  Esta lista se actualiza sola: si creas o activas un usuario en el departamento aparecerá aquí; si lo desactivas, desaparecerá.
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmit}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {editingCategoria ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
